import {
  useGetSchoolGradeGroupsQuery,
  useGetAssessmentTypesQuery,
  useCreateAssessmentTypeMutation,
  useCreateResultTemplateMutation,
} from '@/types/vue-apollo-helpers';
import { useToast } from 'primevue/usetoast';
import { useFormErrors } from '@/composables/useFormErrors';
import {
  ASSESSMENT_PRESETS,
  FEEDBACK_PRESETS,
  buildColumnDrafts,
  buildCreateTemplateInput,
  buildSummaryDrafts,
  deriveAssessmentKeys,
  matchExistingAssessment,
  needsGradeScale,
  normalizeAssessmentName,
  totalMaxScore,
  type WizardAnswers,
  type WizardAssessmentEntry,
  type WizardFeedbackEntry,
  type WizardGradeChoice,
  type WizardStyleCode,
} from '@/utils/resultTemplateWizard';

export type WizardStepId =
  | 'name'
  | 'assessments'
  | 'assessmentDetails'
  | 'total'
  | 'grade'
  | 'gpa'
  | 'gradeScale'
  | 'feedbacks'
  | 'style'
  | 'review';

interface TranscriptEntry {
  stepId: WizardStepId;
  question: string;
  answer: string;
}

export function useResultTemplateWizard(
  emit: (event: 'close' | 'saved') => void,
  isTertiary: { value: boolean },
) {
  const toast = useToast();
  const { serverErrors, handleServerErrors, clearErrors } = useFormErrors();

  // Queries
  const { result: gradeGroupsResult, refetch: refetchGradeGroups } = useGetSchoolGradeGroupsQuery();
  const { result: assessmentTypesResult, refetch: refetchAssessmentTypes } = useGetAssessmentTypesQuery();

  // The grade-scale warning links to the Grade System page in a NEW tab.
  // When the user comes back after creating a scale (or assessments) there,
  // pick up the changes so the warning clears and matching stays current.
  function handleWindowFocus() {
    refetchGradeGroups();
    refetchAssessmentTypes();
  }
  onMounted(() => window.addEventListener('focus', handleWindowFocus));
  onUnmounted(() => window.removeEventListener('focus', handleWindowFocus));

  // Mutations
  const { mutate: createAssessmentType } = useCreateAssessmentTypeMutation();
  const { mutate: createTemplate } = useCreateResultTemplateMutation();

  const gradeGroups = computed(() => gradeGroupsResult.value?.getSchoolGradeGroups || []);
  // Only school-wide (reusable) assessments — term-bound ones would tie the
  // template to a single academic calendar, so the wizard never offers them.
  const reusableAssessments = computed(
    () => (assessmentTypesResult.value?.schoolAssessmentTypes || []).filter(
      (a) => !a.academic_calendar_id,
    ),
  );

  // Answers (defaults are the "easy path" — nudged but always overridable)
  const answers = reactive<WizardAnswers>({
    name: '',
    assessments: [],
    includeTotal: true,
    gradeChoice: 'grade_remark',
    gradeGroupId: null,
    includeGpaPack: true,
    feedbacks: [],
    styleCode: 'classic',
    includeOverallPercentage: true,
  });

  const transcript = ref<TranscriptEntry[]>([]);
  const saving = ref(false);

  // Per-step scratch state
  const nameInput = ref('');
  const detailsError = ref('');
  const selectedFeedbackLabels = ref<string[]>(FEEDBACK_PRESETS.slice(0, 2).map((f) => f.label));
  const customFeedbackInput = ref('');
  const customFeedbacks = ref<WizardFeedbackEntry[]>([]);

  // --- Step machine ---------------------------------------------------------

  const stepSequence = computed<WizardStepId[]>(() => {
    const steps: WizardStepId[] = ['name', 'assessments', 'assessmentDetails'];
    // A Total that sums a single assessment is pointless — skip the question
    // and let grades read straight from that assessment.
    if (answers.assessments.length !== 1) steps.push('total');
    steps.push('grade');
    if (isTertiary.value) steps.push('gpa');
    if (needsGradeScale(answers, isTertiary.value) && gradeGroups.value.length > 1) {
      steps.push('gradeScale');
    }
    steps.push('feedbacks', 'style', 'review');
    return steps;
  });

  const answeredSteps = computed(() => new Set(transcript.value.map((t) => t.stepId)));

  const currentStep = computed<WizardStepId>(() => {
    const next = stepSequence.value.find((step) => !answeredSteps.value.has(step));
    return next || 'review';
  });

  const progress = computed(() => ({
    answered: transcript.value.length,
    total: stepSequence.value.length - 1, // review is the destination, not a question
  }));

  const assessmentLabels = computed(() => answers.assessments.map((a) => a.name.trim()).filter(Boolean));

  const singleGradeGroup = computed(
    () => (gradeGroups.value.length === 1 ? gradeGroups.value[0] ?? null : null),
  );

  // If the school's only grading scale appears AFTER the grade step was
  // answered (e.g. just created on the Grade System page in another tab),
  // link it automatically instead of silently saving without one.
  watch([gradeGroups, () => answers.gradeChoice, () => answers.includeGpaPack], () => {
    if (
      !answers.gradeGroupId
      && singleGradeGroup.value
      && needsGradeScale(answers, isTertiary.value)
    ) {
      answers.gradeGroupId = singleGradeGroup.value.id;
    }
  });

  const currentQuestion = computed<string>(() => {
    switch (currentStep.value) {
    case 'name':
      return 'Hi! 👋 Let\'s set up a result template together — it only takes a minute. First, what should we call it?';
    case 'assessments':
      return `Nice — "${answers.name}" it is. How does your school assess students each term?`;
    case 'assessmentDetails':
      return 'Here\'s the score breakdown — anything tagged \'reusable\' already exists for your school '
        + 'and will be reused, not duplicated. Adjust names or scores if you need to, then confirm.';
    case 'total':
      return `Should I add a Total column that adds up ${assessmentLabels.value.join(' + ')}?`;
    case 'grade':
      return 'Want grades on the report card? I can show a letter grade, and a remark like "Excellent".';
    case 'gpa':
      return 'Since you run a tertiary institution — should I add GPA tracking? That adds Unit, Grade Point and Weighted GP columns, plus GPA & CGPA summaries.';
    case 'gradeScale':
      return 'Which grading scale should I use to work out the grades?';
    case 'feedbacks':
      return 'Almost done! Which comment boxes should appear on the report card?';
    case 'style':
      return 'Last question — pick a look for the printed report card.';
    case 'review':
      return 'That\'s everything! Here\'s your template — check the preview, then create it when you\'re happy.';
    default:
      return '';
    }
  });

  function pushAnswer(stepId: WizardStepId, answer: string) {
    transcript.value.push({ stepId, question: currentQuestion.value, answer });
  }

  function goBack() {
    if (transcript.value.length === 0 || saving.value) return;
    const last = transcript.value.pop();
    if (last?.stepId === 'name') nameInput.value = answers.name;
    detailsError.value = '';
    clearErrors();
  }

  /**
   * Jump straight back to an answered question (clicking its answer bubble).
   * Later answers are discarded from the transcript but their values stay in
   * `answers`, so re-confirming each subsequent step is a single click.
   */
  function jumpBackTo(index: number) {
    if (saving.value) return;
    const target = transcript.value[index];
    if (!target) return;
    transcript.value.splice(index);
    if (target.stepId === 'name') nameInput.value = answers.name;
    detailsError.value = '';
    clearErrors();
  }

  // --- Step answer handlers --------------------------------------------------

  function answerName() {
    const name = nameInput.value.trim();
    if (!name) return;
    answers.name = name;
    pushAnswer('name', `"${name}"`);
  }

  function choosePreset(presetId: string) {
    const preset = ASSESSMENT_PRESETS.find((p) => p.id === presetId);
    if (!preset) return;
    answers.assessments = preset.entries.map((entry) => ({ ...entry }));
    resolveExistingAssessments();
    pushAnswer('assessments', preset.id === 'custom' ? 'I\'ll define it myself' : preset.label);
  }

  function resolveExistingAssessments() {
    answers.assessments.forEach((entry) => {
      const match = matchExistingAssessment(entry.name, reusableAssessments.value);
      const matchedId = match ? match.id : null;
      // Only write on change so the live watcher below converges.
      if (entry.existingId !== matchedId) entry.existingId = matchedId;
      if (match) {
        // Reusing an existing assessment — its configured max score governs scoring.
        if (entry.maxScore !== match.max_score) entry.maxScore = match.max_score;
        if (entry.isExam !== match.is_exam) entry.isExam = match.is_exam;
      }
    });
  }

  // Re-match live as rows are typed, added or removed — and when the
  // reusable assessment list itself (re)loads — so the 'reusable' tag is
  // always current, not just at preset selection time.
  watch(
    [
      () => answers.assessments.map((a) => normalizeAssessmentName(a.name)).join('|'),
      reusableAssessments,
    ],
    () => resolveExistingAssessments(),
  );

  function addAssessmentRow() {
    answers.assessments.push({ name: '', maxScore: 10, isExam: false, existingId: null });
  }

  function removeAssessmentRow(index: number) {
    answers.assessments.splice(index, 1);
  }

  function confirmAssessmentDetails() {
    resolveExistingAssessments();
    const entries = answers.assessments;
    if (entries.length === 0) {
      detailsError.value = 'Add at least one assessment.';
      return;
    }
    if (entries.some((e) => !e.name.trim())) {
      detailsError.value = 'Every assessment needs a name.';
      return;
    }
    if (entries.some((e) => !e.maxScore || e.maxScore < 1)) {
      detailsError.value = 'Every assessment needs a maximum score of at least 1.';
      return;
    }
    const names = entries.map((e) => normalizeAssessmentName(e.name));
    if (new Set(names).size !== names.length) {
      detailsError.value = 'Assessment names must be unique.';
      return;
    }
    detailsError.value = '';
    // With a single assessment the total step is skipped entirely, so make
    // sure no Total column sneaks into the final payload.
    if (entries.length === 1) answers.includeTotal = false;
    pushAnswer(
      'assessmentDetails',
      entries.map((e) => `${e.name.trim()} (${e.maxScore})`).join(' · '),
    );
  }

  function answerTotal(include: boolean) {
    answers.includeTotal = include;
    pushAnswer('total', include ? 'Yes, add a Total column' : 'No total column');
  }

  function answerGrade(choice: WizardGradeChoice) {
    answers.gradeChoice = choice;
    let summary =
      choice === 'grade_remark' ? 'Grade + Remark' : choice === 'grade' ? 'Grade only' : 'No grades';
    if (choice !== 'none' && singleGradeGroup.value) {
      answers.gradeGroupId = singleGradeGroup.value.id;
      summary += ` — using the "${singleGradeGroup.value.name}" scale`;
    }
    pushAnswer('grade', summary);
  }

  function answerGpa(include: boolean) {
    answers.includeGpaPack = include;
    if (include && singleGradeGroup.value) {
      answers.gradeGroupId = singleGradeGroup.value.id;
    }
    pushAnswer('gpa', include ? 'Yes, add GPA tracking' : 'No GPA columns');
  }

  function chooseGradeScale(group: { id: string; name: string }) {
    answers.gradeGroupId = group.id;
    pushAnswer('gradeScale', group.name);
  }

  function toggleFeedback(label: string) {
    const selected = selectedFeedbackLabels.value;
    const index = selected.indexOf(label);
    if (index >= 0) selected.splice(index, 1);
    else selected.push(label);
  }

  function addCustomFeedback() {
    const label = customFeedbackInput.value.trim();
    if (!label) return;
    const exists = [...FEEDBACK_PRESETS, ...customFeedbacks.value]
      .some((f) => f.label.toLowerCase() === label.toLowerCase());
    if (!exists) {
      customFeedbacks.value.push({ label, category: 'general' });
    }
    if (!selectedFeedbackLabels.value.includes(label)) {
      selectedFeedbackLabels.value.push(label);
    }
    customFeedbackInput.value = '';
  }

  const feedbackOptions = computed<WizardFeedbackEntry[]>(() => [
    ...FEEDBACK_PRESETS,
    ...customFeedbacks.value,
  ]);

  function confirmFeedbacks() {
    answers.feedbacks = feedbackOptions.value.filter(
      (f) => selectedFeedbackLabels.value.includes(f.label),
    );
    pushAnswer(
      'feedbacks',
      answers.feedbacks.length > 0
        ? answers.feedbacks.map((f) => f.label).join(', ')
        : 'No comment boxes',
    );
  }

  const styleOptions: Array<{ value: WizardStyleCode; label: string; description: string }> = [
    { value: 'classic', label: 'Classic', description: 'Clean, traditional layout' },
    { value: 'modern', label: 'Modern', description: 'Colourful contemporary look' },
    { value: 'detailed', label: 'Detailed', description: 'Includes a performance chart' },
  ];

  function chooseStyle(style: WizardStyleCode) {
    answers.styleCode = style;
    const option = styleOptions.find((s) => s.value === style);
    pushAnswer('style', option?.label || style);
  }

  // --- Preview ---------------------------------------------------------------

  /**
   * The preview only reflects steps that have been answered, so the sample
   * report card visibly builds up as the conversation progresses.
   */
  const previewAnswers = computed<WizardAnswers>(() => {
    const done = answeredSteps.value;
    return {
      ...answers,
      assessments: done.has('assessmentDetails') ? answers.assessments : [],
      includeTotal: done.has('total') ? answers.includeTotal : false,
      gradeChoice: done.has('grade') ? answers.gradeChoice : 'none',
      includeGpaPack: done.has('gpa') ? answers.includeGpaPack : false,
      feedbacks: done.has('feedbacks') ? answers.feedbacks : [],
      includeOverallPercentage: done.has('total') ? answers.includeOverallPercentage : false,
    };
  });

  const previewColumns = computed(() => buildColumnDrafts(previewAnswers.value, isTertiary.value));
  const previewSummaries = computed(() => buildSummaryDrafts(previewAnswers.value, isTertiary.value));
  const reviewColumns = computed(() => buildColumnDrafts(answers, isTertiary.value));

  const selectedGradeBands = computed(() => {
    const group = gradeGroups.value.find((g) => g.id === answers.gradeGroupId);
    return group?.gradeBands || null;
  });

  const assessmentsToCreate = computed(() =>
    answers.assessments.filter((a) => !a.existingId).map((a) => a.name.trim()),
  );
  const assessmentsToReuse = computed(() =>
    answers.assessments.filter((a) => a.existingId).map((a) => a.name.trim()),
  );

  const detailsMaxTotal = computed(() => totalMaxScore(answers.assessments));

  const showGradeScaleWarning = computed(
    () => needsGradeScale(answers, isTertiary.value)
      && gradeGroups.value.length === 0
      && (currentStep.value === 'gradeScale' || currentStep.value === 'review'),
  );

  // --- Save -------------------------------------------------------------------

  async function saveTemplate() {
    if (saving.value) return;
    saving.value = true;
    clearErrors();
    try {
      // Re-resolve in case reusable assessments changed since the details step.
      resolveExistingAssessments();
      const keys = deriveAssessmentKeys(answers.assessments);
      const idByKey: Record<string, string> = {};

      for (const [index, entry] of answers.assessments.entries()) {
        const key = keys[index];
        if (!key) continue; // keys map 1:1 to assessments
        if (entry.existingId) {
          idByKey[key] = entry.existingId;
          continue;
        }
        // academic_calendar_id stays null so the assessment is REUSABLE
        // (school-wide) rather than bound to the current term.
        const created = await createAssessmentType({
          input: {
            name: entry.name.trim(),
            max_score: entry.maxScore,
            is_ca: !entry.isExam,
            is_exam: entry.isExam,
            academic_calendar_id: null,
            sequence: index,
          },
        });
        const assessment = created?.data?.createAssessmentType;
        if (!assessment) {
          throw new Error(`Could not create the "${entry.name.trim()}" assessment.`);
        }
        idByKey[key] = assessment.id;
      }

      const input = buildCreateTemplateInput(answers, isTertiary.value, idByKey);
      await createTemplate({ input });

      toast.add({
        severity: 'success',
        summary: 'Template created',
        detail: `"${answers.name}" is ready — assign it to ${isTertiary.value ? 'programmes' : 'classes'} whenever you like.`,
        life: 4000,
      });
      emit('saved');
      emit('close');
    } catch (e) {
      handleServerErrors(e);
      const error = e as Error;
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: error.message || 'Failed to create template',
        life: 4000,
      });
    } finally {
      saving.value = false;
    }
  }

  return {
    // state
    answers,
    transcript,
    saving,
    nameInput,
    detailsError,
    selectedFeedbackLabels,
    customFeedbackInput,

    // machine
    currentStep,
    currentQuestion,
    progress,

    // data
    gradeGroups,
    singleGradeGroup,
    feedbackOptions,
    styleOptions,
    assessmentPresets: ASSESSMENT_PRESETS,

    // preview / review
    previewColumns,
    previewSummaries,
    reviewColumns,
    selectedGradeBands,
    assessmentsToCreate,
    assessmentsToReuse,
    detailsMaxTotal,
    showGradeScaleWarning,
    serverErrors,

    // handlers
    answerName,
    choosePreset,
    addAssessmentRow,
    removeAssessmentRow,
    confirmAssessmentDetails,
    answerTotal,
    answerGrade,
    answerGpa,
    chooseGradeScale,
    toggleFeedback,
    addCustomFeedback,
    confirmFeedbacks,
    chooseStyle,
    goBack,
    jumpBackTo,
    saveTemplate,
  };
}

export type { WizardAssessmentEntry };
